import React, { useState } from "react";
import { weeklyRateForRole, rateRecordFor } from "../services/rates";
import { roleColor } from "../data/roleColors";
import { roleFamily } from "../services/roles";

export function fmtMoney(n) {
  if (n == null || isNaN(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1000000) return `$${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  if (a >= 1000) return `$${(n / 1000).toFixed(a >= 100000 ? 0 : 1)}k`;
  return `$${Math.round(n)}`;
}
const DAY = 86400000;
const addWeeks = (start, w) => new Date(start.getTime() + w * 7 * DAY);
const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });

// Time-aware WEEKLY burn model. Each contributor (team member, plus the PX and
// Project Accountant pulled from the project record) burns GC only while active.
// "Events" are the weeks where staffing changes — those are the plotted points.
export function computeBurn(project, members, rates) {
  const budget = Number(project.GCBudget) || 0;
  const start = project.StartDate ? new Date(project.StartDate) : null;
  const end = project.CompletionDate ? new Date(project.CompletionDate) : null;
  if (!budget || !start || !end) return { ok: false, budget, start, end };

  const totalWeeks = Math.max(1, Math.round((end - start) / (7 * DAY)));
  const weekOf = (date) => Math.max(0, Math.min(totalWeeks, Math.round((new Date(date) - start) / (7 * DAY))));

  const contributors = [];
  (members || []).forEach((m) => {
    const alloc = (m.AllocationPercent != null && m.AllocationPercent !== "") ? Number(m.AllocationPercent) : 100;
    const wRate = weeklyRateForRole(m.ProjectRole, rates);
    const rec = rateRecordFor(m.ProjectRole, rates);
    const sW = m.StartDate ? weekOf(m.StartDate) : 0;
    let eW = m.EndDate ? weekOf(m.EndDate) : totalWeeks;
    if (eW <= sW) eW = totalWeeks;
    contributors.push({ name: m.PersonName, role: m.ProjectRole, alloc, weeklyRate: wRate, rateType: rec ? rec.Type : null, weeklyCost: wRate * alloc / 100, startWeek: sW, endWeek: eW, kind: "team" });
  });
  // PX + Project Accountant: if they're already on the team as members (with an
  // editable utilization %), they're counted above. Only fall back to the project
  // record (full duration at 100%) when no such support member exists.
  const hasMember = (fam) => (members || []).some((m) => roleFamily(m.ProjectRole) === fam);
  [["PX", project.ProjectExecutive, "px"], ["Project Accountant", project.ProjectAccountant, "accountant"]].forEach(([role, name, fam]) => {
    if (!name || hasMember(fam)) return;
    const wRate = weeklyRateForRole(role, rates);
    if (wRate <= 0) return;
    const rec = rateRecordFor(role, rates);
    contributors.push({ name, role, alloc: 100, weeklyRate: wRate, rateType: rec ? rec.Type : null, weeklyCost: wRate, startWeek: 0, endWeek: totalWeeks, kind: "support" });
  });

  const evset = new Set([0, totalWeeks]);
  contributors.forEach((c) => { evset.add(c.startWeek); evset.add(c.endWeek); });
  const events = [...evset].filter((w) => w >= 0 && w <= totalWeeks).sort((a, b) => a - b);

  const burnAtWeek = (w) => contributors.reduce((s, c) => s + ((w >= c.startWeek && w < c.endWeek) ? c.weeklyCost : 0), 0);
  const activeAt = (w) => contributors.filter((c) => w >= c.startWeek && w < c.endWeek);
  const remainingAt = (w) => {
    let rem = budget, cur = 0;
    for (let i = 0; i < events.length - 1 && cur < w; i++) {
      const segStart = events[i], segEnd = Math.min(events[i + 1], w);
      if (segEnd > segStart) { rem -= burnAtWeek(segStart) * (segEnd - segStart); cur = segEnd; }
    }
    return rem;
  };
  const projectedEnd = remainingAt(totalWeeks);
  return { ok: true, budget, start, end, totalWeeks, contributors, events, burnAtWeek, activeAt, remainingAt, projectedEnd };
}

// Shared line chart: piecewise burndown through event points, solid (actual) /
// dashed (projected), hoverable event dots, and a labeled Today marker.
function BurnChart({ budget, totalWeeks, remainingAt, events, elapsed, todayVal, hoveredIdx, onHover, leftLabel, rightLabel }) {
  const W = 660, H = 240, padL = 64, padR = 20, padT = 16, padB = 38;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const x = (w) => padL + (Math.max(0, Math.min(w, totalWeeks)) / totalWeeks) * innerW;
  const y = (v) => padT + (1 - Math.max(0, Math.min(budget, v)) / budget) * innerH;
  const remAtFloat = (wf) => {
    // linear interpolation between bracketing events
    let lo = events[0], hi = events[events.length - 1];
    for (let i = 0; i < events.length - 1; i++) { if (events[i] <= wf && wf <= events[i + 1]) { lo = events[i]; hi = events[i + 1]; break; } }
    const f = hi === lo ? 0 : (wf - lo) / (hi - lo);
    return remainingAt(lo) * (1 - f) + remainingAt(hi) * f;
  };
  const tY = todayVal != null ? todayVal : remAtFloat(elapsed);
  const solid = []; const dashed = [];
  events.forEach((w) => { const pt = `${x(w).toFixed(1)},${y(remainingAt(w)).toFixed(1)}`; if (w <= elapsed) solid.push(pt); if (w >= elapsed) dashed.push(pt); });
  solid.push(`${x(elapsed).toFixed(1)},${y(tY).toFixed(1)}`);
  dashed.unshift(`${x(elapsed).toFixed(1)},${y(tY).toFixed(1)}`);
  const area = `${x(0)},${y(0)} ${solid.join(" ")} ${x(elapsed)},${y(0)}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="var(--border)" />
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="var(--border)" />
      <text x={padL - 8} y={y(budget) + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">{fmtMoney(budget)}</text>
      <text x={padL - 8} y={y(0) + 4} textAnchor="end" fontSize="10" fill="var(--text-muted)">$0</text>
      <polygon points={area} fill="rgba(223,171,1,0.16)" />
      <polyline points={solid.join(" ")} fill="none" stroke="var(--gold)" strokeWidth="3" />
      <polyline points={dashed.join(" ")} fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeDasharray="6 5" opacity="0.7" />
      {/* event dots */}
      {events.map((w, i) => (
        <circle key={i} cx={x(w)} cy={y(remainingAt(w))} r={hoveredIdx === i ? 6 : 4}
          fill={w <= elapsed ? "var(--gold)" : "#fff"} stroke="var(--gold)" strokeWidth="2"
          style={{ cursor: "pointer" }} onMouseEnter={() => onHover && onHover(i)} onMouseLeave={() => onHover && onHover(null)} />
      ))}
      {/* today marker */}
      <line x1={x(elapsed)} y1={padT} x2={x(elapsed)} y2={H - padB} stroke="var(--navy-mid)" strokeDasharray="3 3" opacity="0.5" />
      <circle cx={x(elapsed)} cy={y(tY)} r="5" fill="#fff" stroke="var(--navy-mid)" strokeWidth="2.5" />
      <text x={x(elapsed)} y={padT - 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="var(--navy-mid)">Today</text>
      <text x={padL} y={H - padB + 15} textAnchor="start" fontSize="10" fill="var(--text-muted)">{leftLabel}</text>
      <text x={W - padR} y={H - padB + 15} textAnchor="end" fontSize="10" fill="var(--text-muted)">{rightLabel}</text>
    </svg>
  );
}

const ChartLegend = () => (
  <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--text-secondary)", marginTop: 6, flexWrap: "wrap" }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 16, height: 3, background: "var(--gold)", display: "inline-block", borderRadius: 2 }} /> Actual to date</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 16, borderTop: "2px dashed var(--gold)", display: "inline-block" }} /> Projected</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--gold)", display: "inline-block" }} /> Staffing change (hover to inspect)</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: "50%", border: "2px solid var(--navy-mid)", display: "inline-block" }} /> Today</span>
  </div>
);

// ── Single project ──
// `procore` (optional) = latest snapshot totals { original, revised, jtd, forecast }.
// When present, the headline + curve are driven by Procore actuals so the
// burn-down MATCHES Procore: Starting GC = budget, line to today = budget − spent,
// projected to completion = budget − (spent + forecast cost to complete).
// The per-person staffing composition stays as supporting detail.
export default function GCBurnDown({ project, members, rates, procore }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const staffing = computeBurn(project, members, rates);

  const start = project.StartDate ? new Date(project.StartDate) : null;
  const end = project.CompletionDate ? new Date(project.CompletionDate) : null;
  const pBudget = procore ? (Number(procore.original) || Number(procore.revised) || 0) : 0;
  const pJtd = procore ? (Number(procore.jtd) || 0) : 0;
  const pForecast = procore ? (Number(procore.forecast) || 0) : 0;
  const procoreMode = pBudget > 0 && start && end;

  if (!procoreMode && !staffing.ok) {
    return (
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">GC Burn-Down</div></div>
        <div className="empty"><div className="empty-text">Pull the budget from Procore below, or set a <b>Starting GC</b> and project dates in the Admin Console, to see the burn-down.</div></div>
      </div>
    );
  }

  const now = new Date();

  // Build the model that drives the headline + chart.
  let budget, totalWeeks, events, remainingAt, projectedEnd, todayVal, chartEnd;
  if (procoreMode) {
    budget = pBudget;
    totalWeeks = Math.max(1, Math.round((end - start) / (7 * DAY)));
    const elapsedWk = Math.max(0, Math.min(totalWeeks, Math.round((now - start) / (7 * DAY))));
    todayVal = pBudget - pJtd;
    projectedEnd = pBudget - (pJtd + pForecast);
    chartEnd = end;
    const valAt = (w) => (w <= 0 ? pBudget : w >= totalWeeks ? projectedEnd : todayVal);
    events = [...new Set([0, elapsedWk, totalWeeks])].sort((a, b) => a - b);
    remainingAt = (w) => {
      if (w <= events[0]) return valAt(events[0]);
      for (let i = 0; i < events.length - 1; i++) {
        if (w >= events[i] && w <= events[i + 1]) {
          const f = events[i + 1] === events[i] ? 0 : (w - events[i]) / (events[i + 1] - events[i]);
          return valAt(events[i]) * (1 - f) + valAt(events[i + 1]) * f;
        }
      }
      return valAt(events[events.length - 1]);
    };
  } else {
    ({ budget, totalWeeks, events, remainingAt, projectedEnd } = staffing);
    chartEnd = staffing.end;
    const hasCurrent = project.GCRemaining != null && project.GCRemaining !== "";
    todayVal = hasCurrent ? Number(project.GCRemaining) : null;
  }
  const elapsed = Math.max(0, Math.min(totalWeeks, (now - start) / (7 * DAY)));
  const overrun = projectedEnd < 0;

  // Staffing composition (who's burning) — only when the staffing model is valid.
  const sEvents = staffing.ok ? staffing.events : [];
  const curSegFor = (w) => { let idx = 0; for (let i = 0; i < sEvents.length - 1; i++) { if (sEvents[i] <= w && w < sEvents[i + 1]) { idx = i; break; } } return idx; };
  const sElapsed = staffing.ok ? Math.max(0, Math.min(staffing.totalWeeks, (now - start) / (7 * DAY))) : 0;
  const activeIdx = staffing.ok ? (hoveredIdx != null ? Math.min(hoveredIdx, sEvents.length - 1) : curSegFor(sElapsed)) : 0;
  const segStartWk = staffing.ok ? sEvents[activeIdx] : 0;
  const segWeeklyBurn = staffing.ok ? staffing.burnAtWeek(segStartWk) : 0;
  const segPeople = staffing.ok ? [...staffing.activeAt(segStartWk)].sort((a, b) => b.weeklyCost - a.weeklyCost) : [];
  const currentWeeklyBurn = staffing.ok ? staffing.burnAtWeek(Math.min(sEvents[sEvents.length - 1] - 0.0001, Math.floor(sElapsed))) : 0;

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-header">
        <div className="card-title">GC Burn-Down {procoreMode && <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>· live from Procore</span>}</div>
        {staffing.ok && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtMoney(currentWeeklyBurn)}/wk staffing</div>}
      </div>
      <div style={{ display: "flex", gap: 24, marginBottom: 12, flexWrap: "wrap" }}>
        <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Starting GC</div><div style={{ fontSize: 20, fontWeight: 800 }}>{fmtMoney(budget)}</div></div>
        {procoreMode && <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Spent to date</div><div style={{ fontSize: 20, fontWeight: 800 }}>{fmtMoney(pJtd)}</div></div>}
        {procoreMode && <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Forecast to complete</div><div style={{ fontSize: 20, fontWeight: 800 }}>{fmtMoney(pForecast)}</div></div>}
        <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Projected at completion</div><div style={{ fontSize: 20, fontWeight: 800, color: overrun ? "var(--red)" : "var(--green)" }}>{fmtMoney(projectedEnd)}</div></div>
      </div>

      <BurnChart budget={budget} totalWeeks={totalWeeks} remainingAt={remainingAt} events={events} elapsed={elapsed}
        todayVal={todayVal} hoveredIdx={procoreMode ? null : hoveredIdx} onHover={procoreMode ? null : setHoveredIdx}
        leftLabel={fmtDate(start)} rightLabel={fmtDate(chartEnd)} />
      <ChartLegend />
      {overrun
        ? <div style={{ fontSize: 13, color: "var(--red)", marginTop: 8, fontWeight: 600 }}>⚠ Projected overrun of {fmtMoney(Math.abs(projectedEnd))}{procoreMode ? " vs. budget (per Procore forecast)." : " at current staffing."}</div>
        : <div style={{ fontSize: 13, color: "var(--green)", marginTop: 8, fontWeight: 600 }}>On track — projected to finish with {fmtMoney(projectedEnd)} GC remaining.</div>}

      {staffing.ok && <>
      {/* Composition at the selected point */}
      <div className="divider" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--text-muted)" }}>
          Burn composition — {hoveredIdx != null ? "from " + fmtDate(addWeeks(start, segStartWk)) : "today"}
        </div>
        <div style={{ fontSize: 13, fontWeight: 800 }}>{fmtMoney(segWeeklyBurn)}/wk</div>
      </div>
      {segPeople.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No one is burning GC in this window.</div>
      ) : segPeople.map((c, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < segPeople.length - 1 ? "1px solid var(--border)" : "none" }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: roleColor(c.role), flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{c.name}</span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}> · {c.role}{c.kind === "support" ? " (support)" : ""}</span>
          </div>
          <span style={{ fontSize: 11, color: "var(--text-secondary)", width: 110, textAlign: "right" }}>{fmtMoney(c.weeklyRate)}/wk{c.rateType === "Monthly" ? " (mo rate)" : ""}</span>
          <span style={{ fontSize: 11, color: "var(--text-secondary)", width: 44, textAlign: "right" }}>{c.alloc}%</span>
          <span style={{ fontSize: 13, fontWeight: 800, width: 80, textAlign: "right" }}>{fmtMoney(c.weeklyCost)}/wk</span>
        </div>
      ))}
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>{procoreMode ? "Staffing-cost breakdown (estimate). Headline budget & forecast above are live from Procore." : "Hover a dot on the chart to inspect the burn at that staffing change."} Includes PX &amp; Project Accountant from the project record.</div>
      </>}
    </div>
  );
}

// ── Company-wide ──
export function CompanyBurnDown({ projects, teamMembers, rates }) {
  const active = (projects || []).filter((p) => Number(p.GCBudget) > 0 && p.StartDate && p.CompletionDate);
  if (active.length === 0) {
    return (
      <div className="card">
        <div className="card-header"><div className="card-title">Company GC Burn-Down</div></div>
        <div className="empty"><div className="empty-text">No active projects have a Starting GC set yet. Add Starting GC to projects in the Admin Console.</div></div>
      </div>
    );
  }
  const models = active.map((p) => ({ p, start: new Date(p.StartDate), model: computeBurn(p, teamMembers.filter((m) => m.ProjectJobNumber === p.Title), rates) })).filter((m) => m.model.ok);
  const totalBudget = models.reduce((s, m) => s + m.model.budget, 0);
  const minStart = new Date(Math.min(...models.map((m) => m.start.getTime())));
  const maxEnd = new Date(Math.max(...active.map((p) => new Date(p.CompletionDate).getTime())));
  const totalWeeks = Math.max(1, Math.round((maxEnd - minStart) / (7 * DAY)));
  const offWeeks = (s) => Math.round((s - minStart) / (7 * DAY));
  const companyRemainingAt = (g) => models.reduce((sum, { model, start }) => {
    const local = g - offWeeks(start);
    let rem;
    if (local <= 0) rem = model.budget;
    else if (local >= model.totalWeeks) rem = model.projectedEnd;
    else rem = model.remainingAt(local);
    return sum + rem;
  }, 0);
  const companyEnd = companyRemainingAt(totalWeeks);
  const overrun = companyEnd < 0;
  const now = new Date();
  const elapsed = Math.max(0, Math.min(totalWeeks, (now - minStart) / (7 * DAY)));
  const events = [0, ...models.flatMap((m) => [offWeeks(m.start), offWeeks(m.start) + m.model.totalWeeks]), totalWeeks]
    .filter((w) => w >= 0 && w <= totalWeeks).sort((a, b) => a - b);

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Company GC Burn-Down</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{active.length} active projects</div>
      </div>
      <div style={{ display: "flex", gap: 24, marginBottom: 12, flexWrap: "wrap" }}>
        <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Total Starting GC</div><div style={{ fontSize: 22, fontWeight: 800 }}>{fmtMoney(totalBudget)}</div></div>
        <div><div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Projected at completion</div><div style={{ fontSize: 22, fontWeight: 800, color: overrun ? "var(--red)" : "var(--green)" }}>{fmtMoney(companyEnd)}</div></div>
      </div>
      <BurnChart budget={totalBudget} totalWeeks={totalWeeks} remainingAt={companyRemainingAt} events={[...new Set(events)]} elapsed={elapsed}
        leftLabel={fmtDate(minStart)} rightLabel={fmtDate(maxEnd)} />
      <ChartLegend />
      <div className="divider" />
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--text-muted)", marginBottom: 8 }}>By project</div>
      {models.map(({ p, model }, i) => (
        <div key={p.Id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: i < models.length - 1 ? "1px solid var(--border)" : "none" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{p.Title}</span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}> · {p.JobName}</span>
          </div>
          <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{fmtMoney(model.budget)} start</span>
          <span style={{ fontSize: 13, fontWeight: 800, width: 90, textAlign: "right", color: model.projectedEnd < 0 ? "var(--red)" : "var(--green)" }}>{fmtMoney(model.projectedEnd)} end</span>
        </div>
      ))}
    </div>
  );
}
